import React, {
  ReactNode,
  useState,
  useEffect,
  useRef,
  MutableRefObject,
} from 'react';
import {
  withStyles,
  FormControl,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  RadioGroupProps,
  FormControlLabelProps,
  StandardProps,
} from '@material-ui/core';

import MatRadio from '@material-ui/core/Radio';

// onchange example----------------------------------------------------
// export const RadioInputChange = UpdateData => (e, value: string) => {
//     UpdateData({ [e.target.name]: value });
// }

// const----------------------------------------------------
export const EmptyRadioMsg = '請選取其中一個選項。';

// style----------------------------------------------------
export const StyleFormControl = withStyles(() => ({
  root: {
    width: '100%',
  },
}))(FormControl);

export const StyleRadioGroup = withStyles(({ palette }) => ({
  root: {
    '& label:hover': {
      background: palette.background.default,
      borderRadius: '10px',
      '& > *': {
        transition: 'all .3s',
        color: palette.primary.dark,
      },
    },
  },
}))(RadioGroup);

export const StyleFormControlLabel = withStyles({
  root: {
    marginLeft: '0',
    marginRight: '5px',
  },
  label: {
    fontSize: '1em',
  },
})(FormControlLabel);

export const StyleRadio = withStyles(() => ({
  root: {
    padding: '3px',
  },
}))(MatRadio);

export const StyleFormHelperText = withStyles(() => ({
  root: {
    margin: '0 19px',
  },
}))(FormHelperText);

interface IRadioOptions
  extends StandardProps<FormControlLabelProps, '', 'control'> {
  show?: boolean;
  control?: JSX.Element;
}
export interface IRadio extends RadioGroupProps {
  options: IRadioOptions[];
  required?: boolean;
  disabled?: boolean;
  inputRef?: MutableRefObject<HTMLInputElement>;

  error?: boolean;
  helperText?: ReactNode;
  verifyBeforeChange?: boolean;
  verifiers?: IVerifier[];
}

interface IVerifier {
  func: (value: string) => boolean;
  text: string;
}

// component----------------------------------------------------
const Radio = (props: IRadio): JSX.Element => {
  const {
    value,
    required,
    disabled = false,
    inputRef = useRef(),
    verifyBeforeChange = false,
    onChange,
    options = [],
    error,
    helperText,
    verifiers = [],
    ...other
  } = props;
  const [message, setMessage] = useState('');
  const [FirstVerify, setFirstVerify] = useState(verifyBeforeChange);

  useEffect(() => {
    // 始設畫面預設不 verify , 除非 verifyBeforeChange = true
    if (!FirstVerify) {
      setFirstVerify(true);
      setMessage('');
      return;
    }

    // 驗 HTML validation
    if (inputRef.current) {
      const { validationMessage } =
        inputRef.current.getElementsByTagName('input')[0];
      if (validationMessage.length > 0) {
        setMessage(validationMessage);
        return;
      }
    }

    // 驗使用者設定之 verifier, 不驗空值(給 HTML required 驗)
    if (value && verifiers.length > 0) {
      const veriObj = verifiers.find((obj) => obj.func(value));
      if (veriObj) {
        setMessage(veriObj.text);
        return;
      }
    }

    // 先前檢驗都 pass
    setMessage('');
  }, [value, verifyBeforeChange]);

  return (
    <StyleFormControl error={error || message.length > 0}>
      <StyleRadioGroup row value={value} onChange={onChange} {...other}>
        {options
          .filter((obj) => obj.show !== false)
          .map((obj, idx) => {
            const { value: valueTemp, label } = obj;
            let { control = <StyleRadio color="primary" /> } = obj;
            // disabled 時，非 value 的選項要設定 disabled，保持 value 有顏色
            if (disabled && valueTemp !== value) {
              control = React.cloneElement(control, { disabled });
            }
            // 第一個要設定 ref 和 required
            if (idx === 0) {
              control = React.cloneElement(control, {
                ref: inputRef,
                required,
              });
            }
            return (
              <StyleFormControlLabel
                key={String(valueTemp)}
                value={valueTemp}
                control={control}
                label={label}
              />
            );
          })}
      </StyleRadioGroup>
      <StyleFormHelperText>{helperText || message}</StyleFormHelperText>
    </StyleFormControl>
  );
};

export default Radio;
